import { useRef, useState } from "react";
import { Upload, Trash2, FileBox } from "lucide-react";
import { uploadDataset, deleteDataset } from "./api";
import { toast } from "sonner";

const ACCEPT = ".tif,.tiff,.obj,.ply";

export default function Sidebar({ datasets, activeId, onSelect, onReload }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 110 * 1024 * 1024) {
      toast.error("File exceeds 100 MB limit");
      return;
    }
    setBusy(true); setProgress(0);
    try {
      const meta = await uploadDataset(file, setProgress);
      toast.success(`Uploaded ${meta.name}`);
      await onReload();
      onSelect(meta.id);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setBusy(false); setProgress(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setOver(false);
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const removeDataset = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Remove this dataset?")) return;
    try {
      await deleteDataset(id);
      toast.success("Removed");
      await onReload();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  return (
    <>
      <div className="tbg-vw-section" data-testid="viewer-sidebar-upload">
        <h4 className="tbg-vw-section-title">Upload</h4>
        <div
          className={`tbg-vw-drop ${over ? "over" : ""}`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={onDrop}
          data-testid="viewer-dropzone"
        >
          <div className="t">{busy ? "Uploading…" : "Drop file or click"}</div>
          <div className="s">.tif · .tiff · .obj · .ply · ≤ 100 MB</div>
          {busy && (
            <div className="tbg-vw-progress"><div style={{ width: `${progress * 100}%` }} /></div>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          style={{ display: "none" }}
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="viewer-upload-input"
        />
      </div>

      <div className="tbg-vw-section" data-testid="viewer-sidebar-datasets">
        <h4 className="tbg-vw-section-title">
          <span>Datasets</span>
          <span style={{ color: "var(--vw-text)", fontWeight: 700 }}>{datasets.length}</span>
        </h4>
        {datasets.length === 0 && (
          <div style={{ color: "var(--vw-text-dim)", fontSize: 11, fontFamily: "var(--vw-mono)", padding: 6 }}>
            No datasets yet. Drop a file above to begin.
          </div>
        )}
        {datasets.map((ds) => (
          <div
            key={ds.id}
            className={`tbg-vw-ds ${activeId === ds.id ? "active" : ""}`}
            onClick={() => onSelect(ds.id)}
            data-testid={`viewer-dataset-${ds.id === "seed-manitoba-demo" ? "seed" : "item"}`}
          >
            <div className="tbg-vw-ds-name">
              <FileBox size={11} style={{ display: "inline", marginRight: 6, verticalAlign: "-1px", color: "var(--vw-text-dim)" }} />
              {ds.name}
            </div>
            <div className="tbg-vw-ds-meta">
              <span className={`tbg-vw-ds-tag ${ds.seed ? "seed" : ""}`}>
                {ds.seed ? "Demo" : kindShort(ds.kind, ds.file_ext)}
              </span>
              {formatBytes(ds.size_bytes)} · {ds.file_ext.slice(1).toUpperCase()}
            </div>
            {!ds.seed && (
              <button className="tbg-vw-ds-delete" onClick={(e) => removeDataset(e, ds.id)} title="Remove dataset"
                      data-testid={`viewer-dataset-delete-${ds.id}`}>
                <Trash2 size={12} />
              </button>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function kindShort(k, ext) {
  if (k === "geotiff") return "DEM";
  if (k === "mesh") return "Mesh";
  if (k === "pointcloud_or_mesh") return ext === ".ply" ? "PLY" : "Mesh";
  return k;
}

function formatBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
